import { Empty, Modal, Spin, Tag } from "antd";
import { collection, limit, onSnapshot, orderBy, query } from "firebase/firestore";
import React, { useEffect, useMemo, useState } from "react";
import { db } from "../firebase/firebaseApp";
import { formatDateTimeZA } from "../utils/date.util";

const SLIP_LABELS = {
  fuel: "Fuel",
  toll: "Toll",
};

function toMillis(value) {
  if (!value) return null;
  if (typeof value === "number") return value;
  if (typeof value?.toMillis === "function") return value.toMillis();
  if (typeof value?._seconds === "number") return value._seconds * 1000;
  const parsed = Date.parse(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function formatDateTime(value) {
  const millis = toMillis(value);
  return millis ? formatDateTimeZA(millis) : "Upload time pending";
}

function mapSlipSnapshot(snapshot) {
  const data = snapshot.data() || {};
  const type = (data.type || "").toString().toLowerCase();
  return {
    id: snapshot.id,
    ...data,
    type,
    typeLabel: data.typeLabel || `${SLIP_LABELS[type] || "Trip"} slip`,
    imageUrl: data.imageUrl || data.downloadUrl || data.url || "",
    createdAtMs: toMillis(data.createdAt) || toMillis(data.clientCreatedAt),
  };
}

export const TripSlipsPanel = ({ tripId }) => {
  const [loading, setLoading] = useState(Boolean(tripId));
  const [error, setError] = useState("");
  const [slips, setSlips] = useState([]);
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    if (!tripId) {
      setSlips([]);
      setLoading(false);
      return undefined;
    }

    setLoading(true);
    setError("");
    const slipsQuery = query(
      collection(db, "tripOrders", String(tripId), "slips"),
      orderBy("createdAt", "desc"),
      limit(100)
    );

    return onSnapshot(
      slipsQuery,
      (snapshot) => {
        setSlips(snapshot.docs.map(mapSlipSnapshot));
        setLoading(false);
      },
      (err) => {
        setError(err?.message || "Could not load trip slips");
        setLoading(false);
      }
    );
  }, [tripId]);

  const grouped = useMemo(() => ({
    fuel: slips.filter((slip) => slip.type === "fuel"),
    toll: slips.filter((slip) => slip.type === "toll"),
  }), [slips]);

  if (loading) {
    return <div className="flex min-h-[7rem] items-center justify-center"><Spin /></div>;
  }

  if (error) {
    return <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-4 text-sm text-red-700">{error}</div>;
  }

  return <div className="flex flex-col gap-5">
    <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
      <div className="rounded-2xl border border-[#ece5dd] bg-[#fcfaf7] px-4 py-4">
        <span className="block text-xs font-semibold uppercase tracking-[0.18em] text-[#8c7b6b]">Total slips</span>
        <span className="mt-2 block text-2xl font-semibold text-[#161311]">{slips.length}</span>
      </div>
      <div className="rounded-2xl border border-[#ece5dd] bg-[#fcfaf7] px-4 py-4">
        <span className="block text-xs font-semibold uppercase tracking-[0.18em] text-[#8c7b6b]">Fuel slips</span>
        <span className="mt-2 block text-2xl font-semibold text-[#161311]">{grouped.fuel.length}</span>
      </div>
      <div className="rounded-2xl border border-[#ece5dd] bg-[#fcfaf7] px-4 py-4">
        <span className="block text-xs font-semibold uppercase tracking-[0.18em] text-[#8c7b6b]">Toll slips</span>
        <span className="mt-2 block text-2xl font-semibold text-[#161311]">{grouped.toll.length}</span>
      </div>
    </div>

    {!slips.length ? <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No fuel or toll slips uploaded for this trip yet." /> : null}

    <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
      {slips.map((slip) => <button
        type="button"
        key={slip.id}
        onClick={() => setPreview(slip)}
        className="overflow-hidden rounded-2xl border border-[#ece5dd] bg-white p-0 text-left shadow-[0_12px_30px_rgba(15,23,42,0.04)]"
      >
        <div className="aspect-[4/3] bg-[#f5efe9]">
          {slip.imageUrl ? <img src={slip.imageUrl} alt={slip.typeLabel} className="h-full w-full object-cover" /> : <div className="flex h-full items-center justify-center text-sm text-[#8c7b6b]">No image</div>}
        </div>
        <div className="flex flex-col gap-2 px-4 py-3">
          <div className="flex items-center justify-between gap-2">
            <span className="font-semibold text-[#1f1712]">{slip.typeLabel}</span>
            <Tag color={slip.type === "fuel" ? "orange" : "blue"}>{SLIP_LABELS[slip.type] || "Slip"}</Tag>
          </div>
          <span className="text-xs text-[#8c7b6b]">{formatDateTime(slip.createdAt || slip.clientCreatedAt)}</span>
          {slip.driverName ? <span className="text-xs text-[#8c7b6b]">Uploaded by {slip.driverName}</span> : null}
        </div>
      </button>)}
    </div>

    <Modal
      open={Boolean(preview)}
      onCancel={() => setPreview(null)}
      footer={null}
      title={preview?.typeLabel || "Trip slip"}
      width={820}
    >
      <div className="flex flex-col gap-4">
        {preview?.imageUrl ? <img src={preview.imageUrl} alt={preview.typeLabel} className="max-h-[70vh] w-full rounded-2xl object-contain" /> : null}
        <div className="flex flex-wrap items-center justify-between gap-3 text-sm text-[#6b5b4d]">
          <span>{formatDateTime(preview?.createdAt || preview?.clientCreatedAt)}</span>
          {preview?.imageUrl ? <a href={preview.imageUrl} target="_blank" rel="noreferrer" className="text-primary-orange">Open original</a> : null}
        </div>
      </div>
    </Modal>
  </div>;
};
